import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { UploadCloud, Sparkles, Loader2, RefreshCw, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { transformPhoto } from "@/lib/api";
import { LeadForm } from "@/components/site/LeadForm";

export const AITransform = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [kg, setKg] = useState(8);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Wybierz plik graficzny (JPG lub PNG).");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Zdjęcie jest za duże (max 8 MB).");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setStatus("idle");
  };

  const generate = async () => {
    if (!file) {
      toast.error("Najpierw dodaj swoje zdjęcie.");
      return;
    }
    setStatus("loading");
    try {
      const data = await transformPhoto(file, kg);
      setResult(data.image);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Nie udało się wygenerować. Spróbuj ponownie.");
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setStatus("idle");
  };

  return (
    <section id="ai" className="relative py-16 md:py-24 bg-[#0F0E0E]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B6B] flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Przemiana AI · na żywo
            </span>
            <h2 className="font-display font-light text-4xl md:text-6xl mt-6 leading-[0.95]">
              Zobacz siebie{" "}
              <span className="italic text-[#FF6B6B]">po przemianie</span> — zanim zaczniesz
            </h2>
            <p className="mt-6 text-muted text-base md:text-lg leading-relaxed">
              Dodaj swoje zdjęcie, wybierz ile chcesz schudnąć, a nasza AI pokaże Ci poglądową
              wizualizację Twojej metamorfozy. To Twoja motywacja na start.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* LEFT — image / dropzone */}
            <div className="relative border border-[#2A2626] bg-[#1A1718] min-h-[420px] flex items-center justify-center overflow-hidden">
              {!preview && (
                <div
                  data-testid="upload-dropzone"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    pickFile(e.dataTransfer.files?.[0]);
                  }}
                  className="w-full h-full min-h-[420px] flex flex-col items-center justify-center gap-4 cursor-pointer p-10 text-center hover:bg-[#201c1d] transition-colors"
                >
                  <div className="w-16 h-16 rounded-full border border-[#FF6B6B]/40 flex items-center justify-center">
                    <UploadCloud className="w-7 h-7 text-[#FF6B6B]" />
                  </div>
                  <p className="text-lg text-[#F5F2F0]">Dodaj swoje zdjęcie</p>
                  <p className="text-sm text-muted max-w-xs">
                    Najlepiej całej sylwetki, na jasnym tle. Przeciągnij tutaj lub kliknij, aby
                    wybrać. JPG / PNG, max 8 MB.
                  </p>
                </div>
              )}

              {preview && (
                <div className="relative w-full h-full min-h-[420px]">
                  <img
                    src={result && status === "done" ? result : preview}
                    alt="Podgląd"
                    className="w-full h-full object-cover"
                  />
                  {status === "loading" && (
                    <div className="absolute inset-0 bg-[#0F0E0E]/40 backdrop-blur-[2px] overflow-hidden">
                      <div className="scan-line absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-[#FF6B6B]/50 to-transparent" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                        <Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin" />
                        <p className="text-sm text-[#F5F2F0] tracking-wide">
                          Generuję Twoją przemianę...
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-4 left-4 z-10">
                    <span className="text-xs uppercase tracking-[0.2em] bg-[#0F0E0E]/70 backdrop-blur px-3 py-1.5 rounded-full">
                      {result && status === "done" ? "Po (-" + kg + " kg)" : "Przed"}
                    </span>
                  </div>
                </div>
              )}
              <input
                ref={inputRef}
                data-testid="upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </div>

            {/* RIGHT — controls */}
            <div className="border border-[#2A2626] bg-[#1A1718] p-8 md:p-10 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.2em] text-muted">
                    Ile chcesz schudnąć?
                  </label>
                  {preview && (
                    <button
                      onClick={reset}
                      data-testid="reset-button"
                      className="text-xs text-muted hover:text-[#FF6B6B] flex items-center gap-1 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" /> Zmień zdjęcie
                    </button>
                  )}
                </div>

                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-display text-7xl md:text-8xl text-[#FF6B6B] leading-none">
                    {kg}
                  </span>
                  <span className="text-2xl text-muted font-display">kg</span>
                </div>

                <input
                  data-testid="kg-slider"
                  type="range"
                  min="3"
                  max="25"
                  step="1"
                  value={kg}
                  onChange={(e) => setKg(Number(e.target.value))}
                  className="w-full mt-8 accent-[#FF6B6B] cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted mt-2">
                  <span>3 kg</span>
                  <span>25 kg</span>
                </div>
              </div>

              <button
                onClick={generate}
                disabled={status === "loading" || !file}
                data-testid="generate-button"
                className="btn-primary rounded-full mt-10 py-4 px-8 font-semibold text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Generuję...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" /> Generuj moją przemianę
                  </>
                )}
              </button>
              <p className="text-xs text-muted text-center mt-4">
                Podgląd AI ma charakter poglądowy i motywacyjny.
              </p>
            </div>
          </div>
        </Reveal>

        <p
          className="mt-6 text-xs text-muted leading-relaxed max-w-3xl"
          data-testid="ai-privacy-note"
        >
          Zdjęcie zostanie wykorzystane do wygenerowania wizualizacji przy użyciu zewnętrznej
          technologii AI. Nie zapisujemy trwale przesłanego ani wygenerowanego zdjęcia. Szczegóły
          znajdziesz w{" "}
          <Link
            to="/polityka-prywatnosci"
            className="text-[#FF6B6B] hover:underline"
            data-testid="ai-privacy-link"
          >
            Polityce prywatności
          </Link>
          .
        </p>

        {/* Result + Lead form */}
        <AnimatePresence>
          {status === "done" && result && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 grid lg:grid-cols-2 gap-6 lg:gap-8 items-center"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="relative overflow-hidden">
                  <img src={preview} alt="Przed" className="w-full aspect-[3/4] object-cover grayscale" />
                  <span className="absolute bottom-3 left-3 text-xs uppercase tracking-[0.2em] bg-[#0F0E0E]/70 px-3 py-1.5 rounded-full">
                    Przed
                  </span>
                </div>
                <div className="relative overflow-hidden ring-2 ring-[#FF6B6B]">
                  <img src={result} alt="Po" className="w-full aspect-[3/4] object-cover" />
                  <span className="absolute bottom-3 left-3 text-xs uppercase tracking-[0.2em] bg-[#FF6B6B] text-[#0F0E0E] px-3 py-1.5 rounded-full font-semibold">
                    Po -{kg} kg
                  </span>
                </div>
              </div>
              <div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">
                  Podoba Ci się ta wersja siebie?
                </h3>
                <p className="text-muted mt-3 mb-6">
                  Zostaw imię i numer — trenerka oddzwoni i ułoży plan, dzięki któremu naprawdę tam
                  dotrzesz.
                </p>
                <LeadForm goalKg={kg} compact source="ai" />
                <p className="mt-4 text-xs text-muted" data-testid="ai-result-note">
                  Wizualizacja AI ma charakter poglądowy i nie stanowi gwarancji rzeczywistego
                  rezultatu.
                </p>
              </div>
            </motion.div>
          )}
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 border border-[#2A2626] bg-[#1A1718] p-8 flex items-center gap-4 text-muted"
            >
              <ImageOff className="w-6 h-6 text-[#FF6B6B]" />
              Nie udało się wygenerować podglądu. Spróbuj z innym, wyraźniejszym zdjęciem sylwetki.
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
